/**
 * @file Login.tsx
 * @description This file defines the Login component, which serves as the main authentication page for the application.
 * It provides users with the option to log in via MSAL (SSO) or a local username/password form.
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/authProvider";
import { configApi, getBackendUri } from "../../api";
import appLogo from "../../assets/applogo.svg";
import styles from "./Login.module.css";
import { MfaSetup } from "./MfaSetup";

/**
 * @component Login
 * @description A React functional component that renders the user interface for authentication.
 * It handles user input for local login and triggers the appropriate login flow (MSAL or local)
 * by calling the `login` function from the `useAuth` context.
 */
export const Login: React.FC = () => {
    // State to control the visibility of the local username/password form.
    const [showLocalAuth, setShowLocalAuth] = useState(false);
    // State for the username input field.
    const [username, setUsername] = useState("");
    // State for the password input field.
    const [password, setPassword] = useState("");
    // State to store and display any login-related error messages.
    const [error, setError] = useState("");
    // State to manage the MFA step
    const [mfaRequired, setMfaRequired] = useState(false);
    const [mfaUsername, setMfaUsername] = useState("");
    const [totpCode, setTotpCode] = useState("");
    // State for mandatory MFA setup
    const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
    const [setupToken, setSetupToken] = useState("");

    // Hook to access the unified login function from the authentication context.
    const { login } = useAuth();
    // Hook for internationalization.
    const { t } = useTranslation();

    /**
     * @hook useEffect
     * @description On component mount, fetches the application configuration to determine
     * whether the local authentication form should be displayed.
     */
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const config = await configApi();
                // setShowLocalAuth(config.showLocalAuth);
                setShowLocalAuth(true); // Temporarily always show local auth for testing
            } catch (err) {
                console.error("Failed to fetch config:", err);
            }
        };
        fetchConfig();
    }, []);

    useEffect(() => {
        const handleAuthMessage = (event: MessageEvent) => {
            // Ensure the message is from a trusted origin (frontend or backend)
            const backendUri = getBackendUri();
            let backendOrigin = window.location.origin;
            if (backendUri) {
                try {
                    backendOrigin = new URL(backendUri, window.location.origin).origin;
                } catch {
                    backendOrigin = window.location.origin;
                }
            }

            const allowedOrigins = new Set([window.location.origin, backendOrigin]);
            if (!allowedOrigins.has(event.origin)) {
                return;
            }

            try {
                const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
                if (data?.token) {
                    login("token", { token: data.token });
                }
            } catch (e) {
                // Not a JSON message, ignore
            }
        };

        window.addEventListener("message", handleAuthMessage);
        return () => window.removeEventListener("message", handleAuthMessage);
    }, [login]);

    /**
     * @function handleLocalLogin
     * @description Handles the form submission for local authentication.
     * It performs basic validation and calls the `login` function with the 'local' type.
     * @param {React.FormEvent} e - The form event.
     */
    const handleLocalLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        if (!username || !password) {
            setError("Please enter username and password.");
            return;
        }
        try {
            // The login function in AuthProvider will now handle the response
            const result = await login("local", { username, password });
            if (result?.mfa_required) {
                setMfaRequired(true);
                setMfaUsername(result.username || username); // Store username for the MFA step
            } else if (result?.mfa_setup_required && result.setup_token) {
                setMfaSetupRequired(true);
                setSetupToken(result.setup_token);
            }
            // On successful login, the user will be redirected by the main App component
        } catch (err: any) {
            setError(err.message || "Login failed. Please check your credentials.");
        }
    };

    /**
     * @function handleMfaLogin
     * @description Handles the second step of login for MFA-enabled accounts.
     * @param {React.FormEvent} e - The form event.
     */
    const handleMfaLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        if (!totpCode) {
            setError("Please enter your authentication code.");
            return;
        }
        await login("mfa", { username: mfaUsername, totp_code: totpCode });
    };

    /**
     * @function handleMfaSetupSuccess
     * @description Finalizes the login process after mandatory MFA setup is complete.
     * @param {string} finalToken - The full-access JWT.
     */
    const handleMfaSetupSuccess = (finalToken: string) => {
        login("token", { token: finalToken });
    };

    /**
     * @function handleMsalLogin
     * @description Triggers the MSAL (SSO) login flow by calling the `login` function with the 'msal' type.
     */
    const handleMsalLogin = () => {
        login("msal");
    };

    const handleGoogleLogin = () => {
        const backendUri = getBackendUri() || window.location.origin;
        const googleLoginUrl = `${backendUri}/login/google`;
        // Open a popup window for Google login
        window.open(googleLoginUrl, "_blank", "width=500,height=600");
    };

    return (
        <div className={styles.container}>
            <img src={appLogo} alt="App logo" width="150" height="150" className={styles.logo} />
            <h1 className={styles.title}>{t("chatEmptyStateTitle")}</h1>
            <div className={styles.loginOptions}>
                {/* SSO Login Button */}
                <button className={styles.loginButton} onClick={handleMsalLogin}>
                    {t("loginComponent.ssoLoginButton")}
                </button>
                {/* Google Login Button */}
                <button className={styles.loginButton} onClick={handleGoogleLogin} style={{ marginTop: "1rem", backgroundColor: "#DB4437" }}>
                    {t("loginComponent.googleLoginButton")}
                </button>

                {/* Conditionally render the local login or MFA form */}
                {showLocalAuth && (
                    <div className={styles.localAuthContainer}>
                        {mfaSetupRequired ? (
                            <MfaSetup setupToken={setupToken} onSuccess={handleMfaSetupSuccess} />
                        ) : (
                            <>
                                <div className={styles.separator}>
                                    <span>{t("loginComponent.orDivider")}</span>
                                </div>
                                {!mfaRequired ? (
                                    <>
                                        <h3 className={styles.localAuthTitle}>{t("loginComponent.localLoginTitle")}</h3>
                                        <form onSubmit={handleLocalLogin} className={styles.form}>
                                            <div className={styles.inputGroup}>
                                                <label htmlFor="username">{t("loginComponent.usernameLabel")}</label>
                                                <input
                                                    id="username"
                                                    type="text"
                                                    value={username}
                                                    onChange={e => setUsername(e.target.value)}
                                                    required
                                                    className={styles.input}
                                                />
                                            </div>
                                            <div className={styles.inputGroup}>
                                                <label htmlFor="password">{t("loginComponent.passwordLabel")}</label>
                                                <input
                                                    id="password"
                                                    type="password"
                                                    value={password}
                                                    onChange={e => setPassword(e.target.value)}
                                                    required
                                                    className={styles.input}
                                                />
                                            </div>
                                            {error && <p className={styles.error}>{error}</p>}
                                            <button type="submit" className={styles.loginButton}>
                                                {t("loginComponent.loginButton")}
                                            </button>
                                        </form>
                                    </>
                                ) : (
                                    <>
                                        <h3 className={styles.localAuthTitle}>{t("loginComponent.mfaFormTitle")}</h3>
                                        <form onSubmit={handleMfaLogin} className={styles.form}>
                                            <div className={styles.inputGroup}>
                                                <label htmlFor="totp-code">{t("loginComponent.mfaInputLabel")}</label>
                                                <input
                                                    id="totp-code"
                                                    type="text"
                                                    value={totpCode}
                                                    onChange={e => setTotpCode(e.target.value)}
                                                    required
                                                    className={styles.input}
                                                    autoComplete="one-time-code"
                                                />
                                            </div>
                                            {error && <p className={styles.error}>{error}</p>}
                                            <button type="submit" className={styles.loginButton}>
                                                {t("loginComponent.mfaVerifyButton")}
                                            </button>
                                        </form>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
